import React, { memo, type ComponentType } from 'react';
import { EntryPointContainer } from 'react-relay';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { IssueNavigatorJQLBuilderSkeleton } from '@atlassian/jira-skeletons/src/ui/issue-navigator/components/jql-builder.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { JQLBuilderWithAIProps } from './common/types.tsx';

const JQLBuilderWithAIAsync = lazyForPaint<ComponentType<JQLBuilderWithAIProps>>(
	() => import(/* webpackChunkName: "async-jql-builder-with-ai" */ './ui/JQLBuilderWithAI.tsx'),
);

const JQLBuilderWithAIWithRLL: ComponentType<JQLBuilderWithAIProps> = (
	props: JQLBuilderWithAIProps,
) => (
	<Placeholder name="async-jql-builder-with-ai" fallback={<IssueNavigatorJQLBuilderSkeleton />}>
		<JQLBuilderWithAIAsync {...props} />
	</Placeholder>
);

const JQLBuilderWithAIWithEntrypoint = memo<JQLBuilderWithAIProps>((props) => {
	const entryPointReference = props.preloadedEntryPoints?.jqlBuilderWithAiEntryPoint;

	if (entryPointReference) {
		return (
			<UFOSegment name="jql-builder-with-ai-entrypoint">
				<JSErrorBoundary id="jql-builder-with-ai-entrypoint" packageName="jiraJQLBuilderWithAI">
					<EntryPointReferencePlaceholder
						name="jql-builder-with-ai"
						fallback={<IssueNavigatorJQLBuilderSkeleton />}
						entryPointReference={entryPointReference}
					>
						<EntryPointContainer entryPointReference={entryPointReference} props={props} />
					</EntryPointReferencePlaceholder>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}

	return <JQLBuilderWithAIWithRLL {...props} />;
});

const JQLBuilderWithAI = componentWithFG(
	'jql_builder_basic_ssr_stability_fixes',
	JQLBuilderWithAIWithEntrypoint,
	JQLBuilderWithAIWithRLL,
);

export default JQLBuilderWithAI;
